import { Check, CircleSlash } from 'lucide-react';

import { usePermission } from '../../../../auth/AuthContext';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { Tooltip } from '../../../../components/Tooltip';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { StatusText, type StatusTone } from '../../../../components/table/StatusText';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import {
  ATTRITION_CLEARANCE,
  type AttritionClearanceStatus,
  type AttritionRequestClearance,
  type AttritionRequestDetail,
} from '../attritionTypes';

/** A clearance's state on a profile — `pending` is legal here, never on an index surface. */
const CLEARANCE_TONE: Record<AttritionClearanceStatus, StatusTone> = {
  [ATTRITION_CLEARANCE.pending]: 'pending',
  [ATTRITION_CLEARANCE.cleared]: 'active',
  [ATTRITION_CLEARANCE.notCleared]: 'blocked',
};

/** The documents card's own 32px glyph — one control for one line's action, on both cards. */
const GLYPH_BUTTON =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-400 ' +
  'hover:bg-slate-100 disabled:text-slate-300 disabled:hover:bg-transparent';

/**
 * The org units asked to clear the employee, one row per unit — the documents card's shape.
 *
 * Each row states the clearance, who signs it and, on Not cleared, what is still owed. **Clear**
 * and **Not cleared** are the line's OWN signatory's — drawn only where the category names the
 * caller AND they hold `attrition-requests.review`, because the server refuses either alone. A
 * Not cleared line keeps both, so the signatory clears it once the debt is settled.
 *
 * **No Edit**: its state moves by actions, not typing. Absent `onDecide` (Self Service), it is a
 * read of where the employee stands with each unit.
 */
export function AttritionClearancesCard({
  record,
  busy,
  onDecide,
}: {
  record: AttritionRequestDetail;
  busy: boolean;
  onDecide?: (clearance: AttritionRequestClearance, decision: 'clear' | 'not-cleared') => void;
}) {
  const canDecide = usePermission('attrition-requests.review') && onDecide != null;

  const closedReason = record.isOpen
    ? null
    : !record.isActive
      ? 'This request is deleted. Restore it before acting on it.'
      : `This request is ${record.statusLabel.toLowerCase()}.`;

  const rows: PreviewRow[] = record.clearances.map((clearance) => ({
    label: clearance.orgUnitName,
    value: (
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <StatusText tone={CLEARANCE_TONE[clearance.status]}>{clearance.statusLabel}</StatusText>
            <span className="text-xs text-slate-500">
              Signed by {clearance.signatoryName ?? 'an account no longer listed'}
              {clearance.isMine ? ' (you)' : ''}
            </span>
          </span>

          {/* Hidden from a reader who cannot do it; disabled only for STATE, saying why. */}
          {canDecide && clearance.isMine && clearance.status !== ATTRITION_CLEARANCE.cleared ? (
            <span className="flex shrink-0 items-center">
              <Tooltip label={closedReason ?? 'Clear'}>
                <button
                  type="button"
                  id={`attrition-clearance-clear-${clearance.id}`}
                  onClick={() => onDecide?.(clearance, 'clear')}
                  disabled={busy || closedReason != null}
                  aria-label={`Clear for ${clearance.orgUnitName}`}
                  className={GLYPH_BUTTON + ' hover:text-emerald-700'}
                >
                  <Check className="h-4 w-4" aria-hidden="true" />
                </button>
              </Tooltip>
              {clearance.status === ATTRITION_CLEARANCE.pending ? (
                <Tooltip label={closedReason ?? 'Not cleared'}>
                  <button
                    type="button"
                    id={`attrition-clearance-hold-${clearance.id}`}
                    onClick={() => onDecide?.(clearance, 'not-cleared')}
                    disabled={busy || closedReason != null}
                    aria-label={`Not cleared for ${clearance.orgUnitName}`}
                    className={GLYPH_BUTTON + ' hover:text-red-600'}
                  >
                    <CircleSlash className="h-4 w-4" aria-hidden="true" />
                  </button>
                </Tooltip>
              ) : null}
            </span>
          ) : null}
        </div>

        {clearance.status === ATTRITION_CLEARANCE.notCleared && clearance.remarks ? (
          <span className="rounded-md bg-red-50 px-2 py-1 text-xs text-red-800">
            Still owed, per {clearance.decidedByName ?? '—'}: {clearance.remarks}
          </span>
        ) : clearance.status === ATTRITION_CLEARANCE.cleared && clearance.decidedAt != null ? (
          <span className="text-xs text-slate-500">
            Cleared by {clearance.decidedByName ?? '—'} ·{' '}
            <time dateTime={toIso(clearance.decidedAt)} className="font-mono">
              {formatDateTime(clearance.decidedAt)}
            </time>
            {clearance.remarks ? ` — ${clearance.remarks}` : ''}
          </span>
        ) : null}
      </div>
    ),
  }));

  return (
    // Empty, it is prose — the card's own inset, not a hand-rolled one; with rows the table fills it edge to edge.
    <ProfileCard
      id="attrition-request-profile-clearances"
      title="Clearances"
      count={record.clearances.length}
      padded={record.clearances.length === 0}
    >
      {record.clearances.length === 0 ? (
        <p className="text-sm text-slate-500">This request's category asked no org unit to clear the employee.</p>
      ) : (
        <PreviewTable variant="table-display" rows={rows} />
      )}
    </ProfileCard>
  );
}
